/* eslint-disable max-len */
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { atlImageSliderSwiperOptions } from '@atlas-workspace/shared/directives';
import { ModalHelpersService } from '@atlas-workspace/shared/modals';
import { ClientIframePackageDataModel, ClientPackageModel, EPicarioImagePreperty, EPriceType, EWishlistItemType, EWishlistStatus, IframeCategoryItem, ISettingsMenu, LayoutGroup, LayoutProductType, OptionsIframeModel, PicarioDataModel, PicarioOptions, ProductListItemModelCPB, ProductModel } from '@atlas-workspace/shared/models';
import { ModalFacadeService, PicarioService, SharedUiStorageService } from '@atlas-workspace/shared/service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash';
import { filter, finalize, race, Subscription, switchMap, take, tap } from 'rxjs';

import { ProductDetailsModalComponent } from '../../options-catalogue/components/product-details-modal/product-details.component';
import { OptionsIframeService } from '../services/options-iframe.service';

interface IDesignMandatoryFields {
  nr: number;
  id: number;
}

@UntilDestroy()
@Component({
  selector: 'atl-options-iframe-category-details',
  templateUrl: './category-details.component.html',
  styleUrls: ['./category-details.component.scss', './category-details-showroom.component.scss'],
})

export class IframeCategoryDetailsComponent<T extends IDesignMandatoryFields> implements OnInit {
  categoryDetails: OptionsIframeModel | null = null;
  layoutProductTypes: LayoutProductType[] = [];
  activeSelectedType!: LayoutProductType | null;
  imageUrl = '';
  overlayImages: {
    url: string,
    typeId: number,
    id: number
  }[] = [];

  sceneImages: {
    url: string,
    zIndex: number,
    id: number,
    typeId: number | null,
    layoutProductTypeId: number | null
  }[] = [];
  isFirstCategory = true;
  isLastCategory = false;
  loadingTypes = false;
  categoryTotal = 0;
  menu: ISettingsMenu[] = [];
  public isShowroomState = true;

  public isShowroomCategory = true;
  public activeProduct: ProductModel | null = null;
  inProgressTotalPrice = 0;
  readonly wishlistItemType = EWishlistItemType;
  readonly priceType = EPriceType;
  readonly wishlistStatus = EWishlistStatus;
  public readonly companyDescriptionLimit = 100;
  readonly imageSliderSwiperOptions = cloneDeep({ ...atlImageSliderSwiperOptions, pagination: false });
  readonly fileNameLimit = 20;
  loadingProduct = true;
  private readonly defaultImageWith = 1024;
  private readonly defaultImageHeight = 768;

  private categories: IframeCategoryItem[] = [];
  private categoryId!: number;
  private token!: string;
  private activeCategoryData!: IframeCategoryItem | null;
  private picarioOptions!: PicarioOptions;
  private designArr: T[] = [];
  private colorsArr: { id: string, colorNr: number }[] = [];
  private defaultSceneColor = '#D8D8D7';

  public showPrices = true;
  public isNoStandardProduct = false;
  public selectedGroup: LayoutGroup | null = null;
  public contentLoaded = false;
  private selectionToken = '';
  private productModalRef: NgbModalRef | null = null;
  showResetButton = true;

  private _iframeSubscription$ = new Subscription();

  constructor(private readonly picarioService: PicarioService,
    private readonly route: ActivatedRoute,
    private readonly optionsIframeService: OptionsIframeService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalHelpersService: ModalHelpersService,
    private readonly modalFacadeService: ModalFacadeService,
    private readonly sharedUiStorageService: SharedUiStorageService
  ) {
  }

  ngOnInit(): void {
    this.getSelectionToken();
    this.getRouteParams();
    this.getShowPrices();
    this.getTotalPrice();
    this.getPackageDetails();
  }

  private getPackageDetails(): void {
    this.optionsIframeService.packages$
      .pipe(untilDestroyed(this))
      .subscribe((res: ClientIframePackageDataModel | null) => {
        if (!res?.packages?.length) {
          return;
        }
  
        const selectedPackage = res.packages.find((pkg: ClientPackageModel) => pkg.selected);
  
        const shouldNavigateToPackages =
          !selectedPackage &&
          !!res.optionDesignLine &&
          !res.optionDesignLineProducts;
  
        if (!shouldNavigateToPackages) {
          return;
        }
  
        const targetRoute = this.menu.find(item =>
          ['packages', 'introduction'].some(keyword => item.route.includes(keyword))
        )?.route;
  
        if (targetRoute) {
          void this.router.navigate([targetRoute]);
        }
      });
  }
  

  private getPackages(): void {
    this.optionsIframeService.packages$.pipe(
      take(1),
      filter(packageData => !!packageData),
      tap((data) => {
          if (data ) {
            const selectedPackage = data.packages.find(x => x.selected);
            if (selectedPackage) {
              this.showResetButton = false;
            }
          }
      }),
      switchMap(() => this.optionsIframeService.selectionToken$.pipe(take(1))),
      switchMap(selectionToken =>
        this.optionsIframeService.getCategoryDetails(this.token, this.categoryId, selectionToken).pipe(take(1))
      ),
      tap((categoryDetails: OptionsIframeModel) => {
        this.categoryDetails = categoryDetails;
        this.layoutProductTypes = categoryDetails.layoutProductTypes; 
        const allProducts = this.collectAllProducts(this.layoutProductTypes);
        const selectedPicarioProducts = allProducts.filter((x) => x?.selected);
        if (this.activeCategoryData) {
          this.activeCategoryData.products = selectedPicarioProducts;
        }
        this.loadingTypes = false;
        this.contentLoaded = true;
        if (!this.isShowroomCategory) {
          const selectedProduct = allProducts.find(x => x?.selected) || allProducts[0];
          if (selectedProduct) {
            this.getProductDetails(selectedProduct);
          }
        }
  
        this.setTypeProducts();
        this.imageUrl = this.initPicatioListener();
  
        selectedPicarioProducts.forEach(pr => {
          const type = this.layoutProductTypes.find(x => x.id === pr.layoutProductTypeId);
          if (type) {
            this.handleProductSelection(pr, type);
          }
        });
  
        const totalPrice = selectedPicarioProducts.reduce((total, x) => total + Number(x.fullPrice), 0);
        const category = this.categories.find(x => x.id === +this.categoryId);
        if (category) {
          category.totalPrice = totalPrice;
        }
  
      })
    ).subscribe();
  }
  
  private getSelectionToken(): void {
    this.optionsIframeService.selectionToken$.pipe(
      filter(x => !!x),
      untilDestroyed(this)
    )
      .subscribe(token => {
        this.selectionToken = token;
      })
  }

  private getTotalPrice(): void {
    this.optionsIframeService.totalPrice$.pipe(untilDestroyed(this))
    .subscribe((total => {
        this.inProgressTotalPrice = total;
    }));
  }

  public openProductDetailsModal(e: Event, product: ProductListItemModelCPB, type: LayoutProductType): void {
    e.stopImmediatePropagation();
    this.getProductDetails(product, type, true);
  }

  private getRouteParams(): void {
    this.route.params.pipe(untilDestroyed(this)).subscribe(() => {
      this.imageUrl = '';
      this.token = this.route?.parent?.snapshot.params['tokenId'];
      this.categoryId = this.route?.snapshot.params['categoryId'];
      this.categoryDetails = null;
      this.clearStoredData();
      this.loadInitialData();
      this.getPackages();
    });
  }

  private clearStoredData(): void {
    this.activeSelectedType = null;
    this.designArr = [];
    this.colorsArr = [];
    this.sceneImages = [];
    this.overlayImages = [];
    this.activeProduct = null;
    this.isShowroomState = true;
    this.isNoStandardProduct = false;
    this.selectedGroup = null;
  }

  public changeShowroomState(value: boolean): void {
    if (this.isShowroomState === value) {
      return;
    }
    this.isShowroomState = value;

    if (!this.activeSelectedType?.product) {
      const allProducts = this.layoutProductTypes.flatMap(x => x?.products);
      const selectedProduct = allProducts.find(x => x.selected);
      if (selectedProduct) {
        this.getProductDetails(selectedProduct);
      } else {
        this.getProductDetails(allProducts[0]);
      }
      return;
    }

    this.getProductDetails(this.activeSelectedType.product);
  }

  public selectGroup(group: LayoutGroup): void {
    this.selectedGroup = group;
    const wishListProduct = group.products.find((x) => x.selected);
    if (wishListProduct) {
      this.getProductDetails(wishListProduct);
    }
  }

  resetCategory(): void {
    this.activeCategoryData = null;
    const categoryIndex = this.categories.findIndex(x => x.id === +this.categoryId);
    if (~categoryIndex) {
      this.categories[categoryIndex].totalPrice = 0;
      this.categories[categoryIndex].products = [];
    }

    this.clearStoredData();

    this.initTypes(true);
  }

  private loadInitialData(): void {
    this.getCategories();
    this.getNavMenu();
  }

  public openPrevCategory(): void {
    const path = window.location.pathname;
    const currentIndex = this.menu.findIndex((x) => x.route === path);
    if (currentIndex) {
      const prevLink = this.menu[currentIndex - 1].route;
      void this.router.navigate([prevLink]);
    }
  }

  public openNextCategory(): void {
    const path = window.location.pathname;
    const currentIndex = this.menu.findIndex((x) => x.route === path);
    if (currentIndex + 1 === this.menu.length) {
      return;
    }
    const nextLink = this.menu[currentIndex + 1].route;
    void this.router.navigate([nextLink]);
  }

  public moveToSummary(): void {
    void this.router.navigate(['../../summary'], { relativeTo: this.route });
  }

  private setFirstCategory(categories: ISettingsMenu[]): void {
    if (this.categoryId) {
      const [firstItem] = categories;
      const lastItem = categories[categories.length - 1];
      this.isFirstCategory = firstItem?.route.includes(this.categoryId.toString());
      this.isLastCategory = lastItem?.route.includes(this.categoryId.toString());
    }
  }

  private getNavMenu(): void {
    this.optionsIframeService.optionCategories$.pipe(
      untilDestroyed(this)
    )
      .subscribe((res: ISettingsMenu[]) => {
        this.menu = res;
        this.setFirstCategory(res);
      })
  }

  private collectAllProducts(layoutProductTypes: LayoutProductType[]): ProductListItemModelCPB[] {
    return layoutProductTypes.flatMap(layoutProductType => [
      ...(layoutProductType?.products || []),
      ...(layoutProductType?.groups?.flatMap(group => group.products) || [])
    ]);
  }

  private getCategories(): void {
    this.optionsIframeService.categoriesData$.pipe(
      tap(() => {
        this.loadingTypes = true;
      }),
      filter(x => !!x.length),
      take(1),
      finalize(() => {
        this.loadingTypes = false;
        this.contentLoaded = true;
      })
    )
      .subscribe(res => {
        this.categories = res;
        const activeCat = this.categories.find(x => x.id === +this.categoryId);
        this.isShowroomCategory = activeCat?.picario_scene;

        if (activeCat) {
          this.activeCategoryData = activeCat;
          if (activeCat.categoryDetails) {
            this.categoryDetails = activeCat.categoryDetails;
            this.layoutProductTypes = this.categoryDetails.layoutProductTypes;
            const allProducts = this.collectAllProducts(this.layoutProductTypes);
            const selectedPicarioProducts = allProducts.filter((x) => x?.selected);
            this.activeCategoryData.products = selectedPicarioProducts;


            if (!this.isShowroomCategory) {
              const selectedProduct = allProducts.find(x => x?.selected);
              if (selectedProduct) {
                this.getProductDetails(selectedProduct);
              } else {
                const firstProduct = allProducts[0];
                this.getProductDetails(firstProduct);
              }
            }

            this.setTypeProducts();
            this.imageUrl = this.initPicatioListener();

            selectedPicarioProducts.forEach(pr => {
              const type = this.layoutProductTypes.find(x => x.id === pr.layoutProductTypeId);
              if (!type) return;
              this.handleProductSelection(pr, type);
            });
            const totalPrice = selectedPicarioProducts.reduce((total, x) => total + Number(x.fullPrice), 0);
            const category = this.categories.find(x => x.id === +this.categoryId);
            if (category) {
              category.totalPrice = totalPrice;
            }
          }
        } else {
          this.activeCategoryData = null;
        }
        this.cdr.detectChanges();
      })
  }

  private handleProductSelection(product: ProductListItemModelCPB, type: LayoutProductType): void {
    const products = type.grouped
    ? type.groups.flatMap(group => group.products)
    : type.products;
    products.forEach((pr: ProductListItemModelCPB) => {
      pr.selected = pr.id === product.id;
    });

    type.product = product;

    this.setActiveGroup();
  }

  private initTypes(clearItems = false): void {
    if (!clearItems) {
      this.loadingTypes = true;
    }
    this.optionsIframeService.getCategoryDetails(this.token, this.categoryId, clearItems ? '' : this.selectionToken).pipe(
      take(1),
      finalize(() => {
        this.loadingTypes = false;
      })
    )
      .subscribe(res => {
        this.categoryDetails = res;
        this.layoutProductTypes = res.layoutProductTypes;
        if (this.selectionToken && this.activeCategoryData) {
          const selectedPicarioProducts = this.layoutProductTypes.flatMap((productType) => productType.products).filter((x) => x.selected);
          this.activeCategoryData.products = selectedPicarioProducts;
        }
        this.setTypeProducts();
        this.imageUrl = this.initPicatioListener(clearItems);
        this.cdr.detectChanges();
      })
  }

  public setActiveType(type: LayoutProductType): void {
    this.activeSelectedType = type;
  }

  public closeSelectedProductType(): void {
    if(this.selectedGroup) {
      this.selectedGroup = null;
      return;
    }
    this.activeSelectedType = null;
  }

  private setTypeProducts(): void {
    this.layoutProductTypes.forEach((type) => {
      const products = type.grouped
        ? type.groups.flatMap(group => group.products)
        : type.products;
      const wishlistProduct = products.find(product => product.selected);
      if (wishlistProduct) {
        type.product = wishlistProduct;
      } else {
        type.product = products[0];
      }
    });
  }

  private initPicatioListener(clearItems = false): string {
    const allProducts = this.layoutProductTypes.flatMap((productType) => productType.products);
    const picarioProduct = allProducts.find(x => x.picarioData);
    const defaultOptions = {
      scene: picarioProduct?.picarioData.scene.storagePath || '',
      colors: [{ id: this.defaultSceneColor, colorNr: 0 }],
      width: this.defaultImageWith,
      height: this.defaultImageHeight,
      format: 'jpg',
      designArr: [],
    };

    if (clearItems) {
      return this.picarioService.generateImageUrl(defaultOptions) as string;
    }

    const selectedPicarioProducts = this.activeCategoryData ? (this.activeCategoryData.products || []) :
      allProducts.filter(x => x.id === null);
    if (!selectedPicarioProducts?.length) {

      const noStandardProducts = allProducts.filter(x => x.id === null);
      if (this.activeCategoryData) {
        const i = this.categories.findIndex(x => x.id === this.activeCategoryData?.id);
        if (~i) {
          this.categories[i].products = noStandardProducts;
        }
        this.optionsIframeService.updateCategories(this.categories);
      }
      return this.picarioService.generateImageUrl(defaultOptions) as string;
    }

    allProducts.forEach(p => {
      p.selected = selectedPicarioProducts.some(x => x.id === p.id);
      const activeRealProduct = selectedPicarioProducts.find(x => x.id && x.selected);
      const typeIndex = this.layoutProductTypes.findIndex(x => x.id === p.layoutProductTypeId && p.selected);
      if (~typeIndex) {
        this.layoutProductTypes[typeIndex].product = p;
        if (activeRealProduct) {
          this.layoutProductTypes[typeIndex]?.products?.forEach(pr => {
            if (pr.id !== activeRealProduct.id) {
              pr.selected = false;
            }
          })
        }
      }
    })

    selectedPicarioProducts.forEach(pr => {
      if (!pr.id) {
        return;
      }
      const picarioData = pr.picarioData;
      if (!picarioData) {
        return;
      }
      this.updateDesignArray(pr);
      this.picarioOptions = {
        scene: picarioData?.scene?.storagePath,
        colors: this.colorsArr,
        width: this.defaultImageWith,
        height: this.defaultImageHeight,
        format: 'png',
        overlay: '',
        designIndex: picarioData.nr,
        designArr: this.designArr,
        colorNr: picarioData.nr
      }

      if (picarioData?.scene.subscene) {
        this.picarioOptions.overlay = picarioData.scene.subscene.storagePath;
        const subColor = picarioData.color ? {
          id: `${picarioData.color.r}_${picarioData.color.g}_${picarioData.color.b}`,
          nr: picarioData.nr,
        } : undefined;
        const newImageUrl = this.picarioService.generateImageOverlayUrl(this.picarioOptions, picarioData?.scene.subscene.id, subColor) as string;
        if (newImageUrl) {
          const img = new Image();
          img.src = newImageUrl;
          img.onload = (): void => {
            const overlayImg = {
              url: newImageUrl,
              typeId: pr.layoutProductTypeId,
              id: picarioData.scene.subscene.id
            }
            this.overlayImages.push(overlayImg);
          };
        }
      }

      if (picarioData.image) {
        this.setPicarioImages(picarioData, pr);
      }

      if (picarioData.color) {
        this.initColors(picarioData, picarioData.color);
      }
    })

    return this.picarioService.generateImageUrl(this.picarioOptions || defaultOptions) as string;
  }

  private handleNoSrandardDeliveryItems(activeItem?: ProductListItemModelCPB): void {
    const picarioDataToRemove = activeItem?.picarioData || this.activeSelectedType?.product?.picarioData;
    if (picarioDataToRemove) {
      this.removePicarioData(picarioDataToRemove);
    }

  }

  private setActiveGroup(): void {
    this.layoutProductTypes.forEach((type) => {
      if (type.grouped) {
        type.groups?.forEach((group) => {
          group.isActiveGroup = group.products.some((x) => x.selected);
          if (group.isActiveGroup) {
            const selectedProduct = group.products.find((x) => x.selected);
            if (selectedProduct) {
              group.preview = selectedProduct.preview;
              group.groupPrice = selectedProduct.fullPrice;
              group.productTitle = selectedProduct.title;
            }
          } else {
            const [product] = group.products;
            if (product) {
              group.preview = product.preview;
              group.groupPrice = undefined;
              group.productTitle = product.title;
            }
          }
        });
      }
    });

  }

  private getProductDetails(product?: ProductListItemModelCPB, type?: LayoutProductType, openModal = false): void {
    if (!product) {
      return;
    }

    if (!product.id) {
      this.activeProduct = null;
      this.isNoStandardProduct = true;
      this.loadingProduct = false;
      return;
    }
    this.loadingProduct = true;
    this.isNoStandardProduct = false;
    if(this._iframeSubscription$) {
        this._iframeSubscription$.unsubscribe(); 
    }
    this._iframeSubscription$ =  this.optionsIframeService.getIframeProduct(this.token, product.id)
      .pipe(
        take(1),
        finalize(() => {
          setTimeout(() => {
            this.loadingProduct = false;
          }, 500)
        })
      )
      .subscribe((res: ProductModel) => {
        this.activeProduct = res;
        if (openModal) {
          if (this.productModalRef) {
            return;
          }
          this.productModalRef = this.modalFacadeService.openModal(
            ProductDetailsModalComponent,
            {
              ...this.sharedUiStorageService.modalClientCenteredLg,
              modalDialogClass: 'product-details-modal'
            }
          );
          this.productModalRef.componentInstance.product = this.activeProduct;
          this.productModalRef.componentInstance.showPrices = this.showPrices;

          if (product.itemType === EWishlistItemType.NoStandardDelivery) product.productTypeName = type?.name as string;
          this.productModalRef.componentInstance.noStandartDeliveryProduct = product.itemType === EWishlistItemType.NoStandardDelivery ? product : null;
          this.productModalRef.componentInstance.type = type;

          race(this.productModalRef.closed, this.productModalRef.dismissed)
            .pipe(take(1))
            .subscribe(() => {
              this.productModalRef = null;
            });
        }
      });
  }

  public onPreviewDocument(index: number): void {
    this.modalHelpersService.previewImages(this.activeProduct?.fileResourcesAttributes ?? [], index);
  }

  public setActiveDigitalProduct(
    product: ProductListItemModelCPB,
    type: LayoutProductType,
    e?: Event
  ): void {
    e?.stopImmediatePropagation();
    const updateCategoryData = (): void => {
      const allProducts = this.collectAllProducts(this.layoutProductTypes);
      const selectedPicarioProducts = allProducts.filter((x) => x.selected);

      const totalPrice = selectedPicarioProducts.reduce((total, x) => total + Number(x.fullPrice), 0);

      const category = this.categories.find((x) => x.id === +this.categoryId);
      if (category) {
        category.totalPrice = totalPrice;
        category.products = selectedPicarioProducts;
        this.activeCategoryData = category;
        this.optionsIframeService.updateCategories(this.categories);
        this.saveIframeSelectedItems();
      }

      this.updateTotalPrice();
    };

    if (!this.isShowroomCategory || !this.isShowroomState) {
      this.handleProductSelection(product, type);
      this.getProductDetails(product);
      updateCategoryData();
    }

    if (product.itemType !== EWishlistItemType.NoStandardDelivery && product.id) {
      this.handleProductSelection(product, type);
      if (product.picarioData) {
        this.productUpdatePicarioData(product, type);
      }
    } else {
      const activeItem = type?.products.find((x) => x.selected);
      this.handleNoSrandardDeliveryItems(activeItem);
      this.handleProductSelection(product, type);
    }

    updateCategoryData();
  }

    private saveIframeSelectedItems(): void {
      this.optionsIframeService.categoriesData$.pipe(take(1)).subscribe((res: IframeCategoryItem[]) => {
        const selectedItems = res
          .flatMap((cat) => cat.products ?? [])
          .filter((product) => product)
          .map(({ layoutProductTypeId, id }) => ({
            layout_product_type_id: layoutProductTypeId,
            layout_product_type_item_id: id,
          }));
  
        this.optionsIframeService
          .saveIframeSelectedItems(this.token, '', selectedItems ?? [], this.selectionToken)
          .pipe(take(1))
          .subscribe((token: string) => {
            this.selectionToken = token;
          });
      });
    }

  private updateTotalPrice(): void {
    const totalCategoriesPrice = this.categories.reduce((total, x) => total + Number(x.totalPrice || 0), 0);
    this.optionsIframeService.setTotalPrice(totalCategoriesPrice);
  }

  private removePicarioData(picarioData: PicarioDataModel): void {
    this.removeSceneImage(picarioData);
    this.removeDesign(picarioData);
    this.removeColor(picarioData);
    this.removeOverlay(picarioData);

    this.imageUrl = this.picarioService.generateImageUrl(this.picarioOptions) as string;
  }

  private removeSceneImage(picarioData: PicarioDataModel): void {
    if (picarioData.image && this.sceneImages.length) {
      const index = this.sceneImages.findIndex(x => x.id === picarioData.image?.id);
      if (index !== -1) {
        this.sceneImages.splice(index, 1);
      }
    }
  }

  private removeDesign(picarioData: PicarioDataModel): void {
    if (picarioData.design && this.designArr.length) {
      const index = this.designArr.findIndex(x => x.id === picarioData.design.id);
      if (index !== -1) {
        this.designArr.splice(index, 1);
        this.picarioOptions.designArr = [...this.designArr];
      }
    }
  }

  private removeColor(picarioData: PicarioDataModel): void {
    if (this.picarioOptions.colors?.length && picarioData.color) {
      const index = this.colorsArr.findIndex(x => x.colorNr === picarioData.nr);
      if (index !== -1) {
        this.colorsArr.splice(index, 1);
        this.picarioOptions.colors = [...this.colorsArr];
      }
    }
  }

  private removeOverlay(picarioData: PicarioDataModel): void {
    if (this.picarioOptions.overlay && picarioData.scene?.subscene) {
      const index = this.overlayImages.findIndex(x => x.id === picarioData.scene.subscene.id);
      if (index !== -1) {
        this.overlayImages.splice(index, 1);
      }
    }
  }

  private updateDesignArray(product: ProductListItemModelCPB): void {
    const design = product.picarioData?.design;
    if (!design) return;

    const existingIndex = this.designArr.findIndex(item => item.nr === product.picarioData.nr);
    design['nr'] = product.picarioData.nr;

    if (existingIndex !== -1) {
      this.designArr[existingIndex] = design;
    } else {
      this.designArr.push(design);
    }
  }

  private productUpdatePicarioData(product: ProductListItemModelCPB, type: LayoutProductType): void {
    this.updateDesignArray(product);
    const color = product.picarioData?.color;
    if (color) {
      this.initColors(product.picarioData, color);
    }
    this.picarioOptions = {
      scene: product.picarioData?.scene?.storagePath || '',
      colors: this.colorsArr,
      width: this.defaultImageWith,
      height: this.defaultImageHeight,
      format: 'png',
      overlay: '',
      designIndex: product.picarioData.nr,
      designArr: this.designArr,
      colorNr: product.picarioData.nr
    }
    if (product.picarioData?.scene.subscene) {
      const imageIndex = this.sceneImages.findIndex(x => x.layoutProductTypeId === product.layoutProductTypeId);
      if (~imageIndex) {
        this.sceneImages.splice(imageIndex, 1);
      }
      this.picarioOptions.overlay = product.picarioData.scene.subscene.storagePath;
      const subColor = product.picarioData.color ? {
        id: `${product.picarioData.color.r}_${product.picarioData.color.g}_${product.picarioData.color.b}`,
        nr: product.picarioData.nr,
      } : undefined;
      const newImageUrl = this.picarioService.generateImageOverlayUrl(this.picarioOptions, product.picarioData?.scene.subscene.id, subColor) as string;
      if (newImageUrl) {
        const img = new Image();
        img.src = newImageUrl;
        img.onload = (): void => {
          const overlayImg = {
            url: newImageUrl,
            typeId: type.id,
            id: product.picarioData.scene.subscene.id
          }
          const existingIndex = this.overlayImages.findIndex(x => x.typeId === type.id);
          if (~existingIndex) {
            this.overlayImages[existingIndex] = overlayImg;
          } else {
            this.overlayImages.push(overlayImg);
          }
        };
        return;
      }
    }

    if (product.picarioData?.image) {
      const overlayIndex = this.overlayImages.findIndex(x => x.typeId === type.id);
      if (~overlayIndex) {
        this.overlayImages.splice(overlayIndex, 1);
      }
      this.setPicarioImages(product.picarioData, product);
      return;
    }
    this.imageUrl = this.picarioService.generateImageUrl(this.picarioOptions) as string;

  }

  private setPicarioImages(picarioData: PicarioDataModel, product?: ProductListItemModelCPB): void {
    if (!picarioData.image) {
      return;
    }
    this.picarioOptions.overlay = picarioData.image?.storagePath;

    const zIndexObject = picarioData.image.properties.find(item => item.name === EPicarioImagePreperty.ZIndex);
    const sceneImage = {
      url: (this.picarioService.generateImageOverlayUrl(this.picarioOptions) as string).replace('format=jpg', 'format=png'),
      zIndex: zIndexObject ?? 3,
      id: picarioData.image.id,
      typeId: product ? product.type.id : null,
      layoutProductTypeId: product ? product.layoutProductTypeId : null,
    }
    const isDuplicate = this.sceneImages.some(image => image.id === sceneImage.id);
    if (!isDuplicate) {
      if (sceneImage.typeId) {
        const i = this.sceneImages.findIndex(x => x.typeId === sceneImage.typeId);
        if (~i) {
          this.sceneImages.splice(i, 1);
        }
      }
      this.sceneImages.push(sceneImage);
    }
  }

  private initColors(picarioData: PicarioDataModel, color: any): void {
    if (picarioData.scene.subscene) {
      return;
    }
    const colorToAdd = {
      id: `${color.r}_${color.g}_${color.b}`,
      colorNr: picarioData.nr
    }
    if (!this.colorsArr?.length) {
      this.colorsArr.push(colorToAdd);
    } else {
      const index = this.colorsArr.findIndex(x => x.colorNr === colorToAdd.colorNr);
      if (~index) {
        this.colorsArr[index] = colorToAdd;
      } else {
        this.colorsArr.push(colorToAdd);
      }
    }
  }

  private getShowPrices(): void {
    this.optionsIframeService.showPrices$.pipe(untilDestroyed(this))
      .subscribe((showPrices: boolean) => {
        this.showPrices = showPrices;
      })
  }

  afterInitSwiperHandler(): void {
    queueMicrotask(() => this.cdr.detectChanges());
  }
}
